import { useState, useEffect } from "react";
import api from "../api/api";

// ─── Constantes ───────────────────────────────────────────────────────────────
const DIAS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const PROVINCIAS = [
  "Buenos Aires", "CABA", "Córdoba", "Santa Fe", "Mendoza",
  "Tucumán", "Entre Ríos", "Salta", "Misiones", "Chaco",
];

const STEPS = [
  { label: "Perfil",       icon: "👤" },
  { label: "Servicios",    icon: "🛠️" },
  { label: "Cobertura",    icon: "📍" },
  { label: "Horarios",     icon: "📅" },
];

const SERVICE_ICONS = [
  "🔧","⚡","🔥","🎨","🧱","🔑","🧹","🌿",
  "❄️","🏗️","🐛","👶","👴","💇","🐾","🛋️",
  "🏛️","🎨","💪","🔒","💻","🎉","⚒️","🔩","🧺","🚗","🏊",
];

// ─── Shared styles ────────────────────────────────────────────────────────────
const inputStyle = {
  padding: "11px 14px", borderRadius: 8,
  border: "1.5px solid #cddae4", fontSize: 14,
  fontFamily: "Montserrat, sans-serif",
  color: "#163244", background: "#fff", outline: "none",
  transition: "border-color 0.2s",
  width: "100%", boxSizing: "border-box",
};

// ─── Sub-components ───────────────────────────────────────────────────────────
function StepIndicator({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0, marginBottom: 40 }}>
      {STEPS.map((s, i) => {
        const done   = i < current;
        const active = i === current;
        const isLast = i === STEPS.length - 1;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <div style={{
                width: 48, height: 48, borderRadius: "50%",
                background: done ? "#E7A432" : active ? "#163244" : "transparent",
                border: `2.5px solid ${done || active ? (done ? "#E7A432" : "#163244") : "#c8d4dc"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: active ? 20 : 17, transition: "all 0.3s ease",
                boxShadow: active ? "0 0 0 4px rgba(22,50,68,0.12)" : "none",
              }}>
                {done
                  ? <span style={{ color: "#fff", fontWeight: 700, fontSize: 18 }}>✓</span>
                  : <span style={{ filter: active ? "none" : "grayscale(1) opacity(0.5)" }}>{s.icon}</span>
                }
              </div>
              <span style={{
                fontSize: 11, fontWeight: active ? 700 : 500,
                color: active ? "#163244" : done ? "#E7A432" : "#8fa3b1",
                letterSpacing: "0.04em", textTransform: "uppercase",
              }}>
                {s.label}
              </span>
            </div>
            {!isLast && (
              <div style={{
                width: 64, height: 2, marginBottom: 22, marginLeft: 4, marginRight: 4,
                background: done ? "#E7A432" : "#e0e9ef", transition: "background 0.3s ease",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function FormField({ label, error, children, required }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 0 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#163244", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        {label}{required && <span style={{ color: "#E7A432", marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {error && <span style={{ fontSize: 11, color: "#e53935", fontWeight: 500 }}>⚠ {error}</span>}
    </div>
  );
}

// ─── Step 1: Perfil ───────────────────────────────────────────────────────────
function StepPerfil({ data, onChange, errors }) {
  return (
    <div>
      <h3 style={{ margin: "0 0 6px", color: "#163244", fontSize: 20, fontWeight: 700 }}>Datos del perfil</h3>
      {/* Decía "Esta información será visible para los clientes." y era falso
          al revés (30-10-2026): el nombre legal, el DNI y el CUIT no los ve
          ningún cliente. El teléfono de este paso NO se guarda
          (ALTA-PRESTADOR-TELEFONO-NO-SE-GUARDA) y la presentación no la muestra
          ninguna pantalla de cliente: por eso el texto no los nombra. */}
      <p style={{ margin: "0 0 28px", color: "#5c7a8a", fontSize: 14 }}>Los clientes ven el nombre para mostrar. El nombre legal, el DNI y el CUIT son de uso interno.</p>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <FormField label="Nombre para mostrar" required error={errors.display_name}>
          <input style={inputStyle} placeholder="Ej: Juan Pérez Plomería" value={data.display_name} onChange={e => onChange("display_name", e.target.value)} />
        </FormField>
        <FormField label="Nombre legal" error={errors.legal_name}>
          <input style={inputStyle} placeholder="Nombre completo o razón social" value={data.legal_name} onChange={e => onChange("legal_name", e.target.value)} />
        </FormField>
      </div>
      <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
        <FormField label="DNI" required error={errors.dni}>
          <input style={inputStyle} placeholder="Sin puntos ni espacios" value={data.dni} onChange={e => onChange("dni", e.target.value.replace(/\D/g, ""))} maxLength={8} />
        </FormField>
        <FormField label="CUIT / CUIL" error={errors.cuit}>
          <input style={inputStyle} placeholder="XX-XXXXXXXX-X" value={data.cuit} onChange={e => onChange("cuit", e.target.value)} />
        </FormField>
        <FormField label="Teléfono" required error={errors.phone}>
          <input style={inputStyle} placeholder="+54 9 11 XXXX-XXXX" value={data.phone} onChange={e => onChange("phone", e.target.value)} />
        </FormField>
      </div>
      <FormField label="Presentación" error={errors.bio}>
        <textarea
          style={{ ...inputStyle, resize: "vertical", minHeight: 100, lineHeight: 1.6 }}
          placeholder="Contale a los clientes quién sos, tu experiencia y especialidades..."
          value={data.bio} onChange={e => onChange("bio", e.target.value)} maxLength={500}
        />
        <span style={{ fontSize: 11, color: "#8fa3b1", textAlign: "right", marginTop: 2 }}>{data.bio.length}/500</span>
      </FormField>
    </div>
  );
}

// ─── Step 2: Servicios ────────────────────────────────────────────────────────
function StepServicios({ data, onChange, errors, catalogo, loadingCatalog }) {
  const toggle = (id) => {
    const curr = data.services || [];
    const next = curr.includes(id) ? curr.filter(x => x !== id) : [...curr, id];
    onChange("services", next);
  };

  if (loadingCatalog) {
    return (
      <div style={{ textAlign: "center", padding: "48px", color: "#8fa3b1" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        Cargando catálogo de servicios...
      </div>
    );
  }

  return (
    <div>
      <h3 style={{ margin: "0 0 6px", color: "#163244", fontSize: 20, fontWeight: 700 }}>¿Qué servicios ofrecés?</h3>
      <p style={{ margin: "0 0 28px", color: "#5c7a8a", fontSize: 14 }}>
        Seleccioná todos los rubros en los que trabajás. Luego cargarás documentación por cada uno.
      </p>
      {errors.services && (
        <div style={{ background: "#fff3e0", border: "1px solid #E7A432", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#b06a00" }}>
          ⚠ {errors.services}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
        {catalogo.map((s, idx) => {
          const sel = (data.services || []).includes(s.id);
          return (
            <button
              key={s.id}
              onClick={() => toggle(s.id)}
              style={{
                padding: "14px 12px", borderRadius: 10, cursor: "pointer",
                border: sel ? "2px solid #E7A432" : "2px solid #e0e9ef",
                background: sel ? "#fffbf2" : "#f7fafc",
                color: sel ? "#163244" : "#5c7a8a",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: sel ? 700 : 500, fontSize: 14, textAlign: "center",
                transition: "all 0.2s",
                boxShadow: sel ? "0 2px 8px rgba(231,164,50,0.18)" : "none",
              }}
            >
              <div style={{ fontSize: 24, marginBottom: 6 }}>
                {SERVICE_ICONS[idx % SERVICE_ICONS.length]}
              </div>
              {s.service_name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step 3: Cobertura ────────────────────────────────────────────────────────
function StepCobertura({ data, onChange, errors }) {
  const zonas = data.coverage || [];

  const addZona = () => {
    onChange("coverage", [...zonas, { provincia: "", ciudad: "", barrio: "", codigo_postal: "", radio_km: 5 }]);
  };

  const updateZona = (i, field, val) => {
    onChange("coverage", zonas.map((z, idx) => idx === i ? { ...z, [field]: val } : z));
  };

  const removeZona = (i) => {
    onChange("coverage", zonas.filter((_, idx) => idx !== i));
  };

  return (
    <div>
      <h3 style={{ margin: "0 0 6px", color: "#163244", fontSize: 20, fontWeight: 700 }}>Zonas de cobertura</h3>
      <p style={{ margin: "0 0 24px", color: "#5c7a8a", fontSize: 14 }}>Indicá en qué áreas trabajás. Podés agregar varias zonas.</p>

      {zonas.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 20px", background: "#f7fafc", borderRadius: 12, border: "2px dashed #cddae4", marginBottom: 16, color: "#8fa3b1", fontSize: 14 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📍</div>
          Todavía no agregaste ninguna zona.
        </div>
      )}

      {zonas.map((z, i) => (
        <div key={i} style={{ background: "#f7fafc", borderRadius: 12, padding: 16, border: "1.5px solid #e0e9ef", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "#163244", background: "#e7f0f5", padding: "3px 10px", borderRadius: 20 }}>
              Zona {i + 1}
            </span>
            <button onClick={() => removeZona(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", fontSize: 18, padding: 4 }}>✕</button>
          </div>
          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <FormField label="Provincia" required>
              <select style={{ ...inputStyle, cursor: "pointer" }} value={z.provincia} onChange={e => updateZona(i, "provincia", e.target.value)}>
                <option value="">Seleccioná...</option>
                {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </FormField>
            <FormField label="Ciudad">
              <input style={inputStyle} placeholder="Ej: Mar del Plata" value={z.ciudad} onChange={e => updateZona(i, "ciudad", e.target.value)} />
            </FormField>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <FormField label="Barrio / Localidad">
              <input style={inputStyle} placeholder="Opcional" value={z.barrio} onChange={e => updateZona(i, "barrio", e.target.value)} />
            </FormField>
            <FormField label="Código postal">
              <input style={inputStyle} placeholder="Ej: 1640" value={z.codigo_postal} onChange={e => updateZona(i, "codigo_postal", e.target.value)} />
            </FormField>
            <FormField label={`Radio: ${z.radio_km} km`}>
              <input type="range" min={1} max={50} step={1} value={z.radio_km}
                onChange={e => updateZona(i, "radio_km", Number(e.target.value))}
                style={{ width: "100%", accentColor: "#E7A432", marginTop: 10 }}
              />
            </FormField>
          </div>
        </div>
      ))}

      {errors.coverage && <span style={{ fontSize: 12, color: "#e53935" }}>⚠ {errors.coverage}</span>}

      <button
        onClick={addZona}
        style={{
          marginTop: 8, padding: "11px 20px", borderRadius: 8,
          border: "2px dashed #163244", background: "transparent",
          color: "#163244", fontFamily: "Montserrat, sans-serif",
          fontWeight: 600, fontSize: 14, cursor: "pointer", width: "100%",
          transition: "all 0.2s",
        }}
        onMouseEnter={e => { e.target.style.background = "#163244"; e.target.style.color = "#fff"; }}
        onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = "#163244"; }}
      >
        + Agregar zona
      </button>
    </div>
  );
}

// ─── Step 4: Disponibilidad ───────────────────────────────────────────────────
function StepDisponibilidad({ data, onChange }) {
  const avail = data.availability || DIAS.map((_, i) => ({
    day: i, start: "09:00", end: "18:00", available: i >= 1 && i <= 5,
  }));

  const update = (i, field, val) => {
    onChange("availability", avail.map((d, idx) => idx === i ? { ...d, [field]: val } : d));
  };

  return (
    <div>
      <h3 style={{ margin: "0 0 6px", color: "#163244", fontSize: 20, fontWeight: 700 }}>Disponibilidad semanal</h3>
      <p style={{ margin: "0 0 28px", color: "#5c7a8a", fontSize: 14 }}>Configurá tus días y horarios de trabajo habituales.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {avail.map((d, i) => (
          <div key={i} style={{
            display: "flex", alignItems: "center", gap: 12,
            padding: "12px 16px", borderRadius: 10,
            background: d.available ? "#f7fafc" : "#fafafa",
            border: `1.5px solid ${d.available ? "#E7A432" : "#e0e9ef"}`,
            opacity: d.available ? 1 : 0.55, transition: "all 0.2s",
          }}>
            <div onClick={() => update(i, "available", !d.available)} style={{
              width: 44, height: 24, borderRadius: 12, cursor: "pointer",
              background: d.available ? "#163244" : "#c8d4dc",
              position: "relative", transition: "background 0.25s", flexShrink: 0,
            }}>
              <div style={{
                position: "absolute", top: 3, left: d.available ? 23 : 3,
                width: 18, height: 18, borderRadius: "50%",
                background: d.available ? "#E7A432" : "#fff", transition: "left 0.25s",
              }} />
            </div>
            <span style={{ width: 36, fontWeight: 700, fontSize: 13, color: d.available ? "#163244" : "#8fa3b1" }}>
              {DIAS[i]}
            </span>
            {d.available ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                  <input type="time" value={d.start} onChange={e => update(i, "start", e.target.value)}
                    style={{ ...inputStyle, width: "auto", padding: "6px 10px", fontSize: 13 }} />
                  <span style={{ color: "#8fa3b1", fontSize: 12 }}>hasta</span>
                  <input type="time" value={d.end} onChange={e => update(i, "end", e.target.value)}
                    style={{ ...inputStyle, width: "auto", padding: "6px 10px", fontSize: 13 }} />
                </div>
              </>
            ) : (
              <span style={{ fontSize: 13, color: "#aab8c2", fontStyle: "italic" }}>No disponible</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ProviderRegisterPage() {
  const [step,       setStep]       = useState(0);
  const [submitted,  setSubmitted]  = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [errors,     setErrors]     = useState({});
  const [apiError,   setApiError]   = useState("");
  const [catalogo,   setCatalogo]   = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  const [form, setForm] = useState({
    display_name: "", legal_name: "", dni: "", cuit: "",
    phone: "", bio: "",
    services: [],
    coverage: [],
    availability: DIAS.map((_, i) => ({
      day: i, start: "09:00", end: "18:00", available: i >= 1 && i <= 5,
    })),
  });

  // Cargar catálogo desde GET /providers/catalog
  useEffect(() => {
    setLoadingCatalog(true);
    api.get("/providers/catalog")
      .then(res => {
        // El endpoint devuelve { services: [...] } o array directo
        const data = res.data.services || res.data;
        setCatalogo(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        console.error("Error cargando catálogo:", err);
        setApiError("No se pudo cargar el catálogo de servicios.");
      })
      .finally(() => setLoadingCatalog(false));
  }, []);

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) setErrors(e => ({ ...e, [field]: null }));
    setApiError("");
  };

  const validate = () => {
    const e = {};
    if (step === 0) {
      if (!form.display_name.trim()) e.display_name = "El nombre para mostrar es obligatorio";
      if (!form.dni.trim())          e.dni = "El DNI es obligatorio";
      if (!form.phone.trim())        e.phone = "El teléfono es obligatorio";
    }
    if (step === 1) {
      if (!form.services.length) e.services = "Seleccioná al menos un servicio";
    }
    if (step === 2) {
      if (!form.coverage.length) e.coverage = "Agregá al menos una zona de cobertura";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validate()) return;
    if (step < STEPS.length - 1) setStep(s => s + 1);
    else handleSubmit();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setApiError("");

    try {
      // 1. Crear perfil
      await api.post("/providers/register", {
        display_name: form.display_name,
        legal_name:   form.legal_name,
        dni:          form.dni,
        cuit:         form.cuit,
        phone:        form.phone,
        bio:          form.bio,
      });

      // 2. Registrar servicios seleccionados
      for (const serviceId of form.services) {
        await api.post("/providers/me/services", { service_id: serviceId });
      }

      // 3. Registrar zonas de cobertura
      for (const zona of form.coverage) {
        await api.post("/providers/me/coverage", zona);
      }

      // 4. Registrar disponibilidad
      // El backend espera day_of_week/start_time/end_time; el formulario usa
      // day/start/end. Hasta el 26-10-2026 se mandaba tal cual y el backend
      // borraba todo y salteaba cada horario: el prestador quedaba sin ninguno.
      await api.put("/providers/me/availability", {
        availability: form.availability.map(d => ({
          day_of_week: d.day, start_time: d.start, end_time: d.end, available: d.available,
        })),
      });

      setSubmitted(true);

    } catch (err) {
      console.error("Error en registro:", err);
      const msg = err.response?.data?.error || "Ocurrió un error al registrar. Intentá de nuevo.";
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: "#f0f5f8", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Montserrat, sans-serif" }}>
        <div style={{ background: "#fff", borderRadius: 20, padding: "56px 48px", maxWidth: 480, width: "90%", textAlign: "center", boxShadow: "0 8px 40px rgba(22,50,68,0.10)" }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#163244", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 36, color: "#E7A432", fontWeight: 800 }}>✓</div>
          <h2 style={{ color: "#163244", fontSize: 24, fontWeight: 800, margin: "0 0 12px" }}>¡Registramos tu perfil!</h2>
          <p style={{ color: "#5c7a8a", fontSize: 15, lineHeight: 1.6, margin: "0 0 32px" }}>
            Tu perfil quedó en estado <strong style={{ color: "#E7A432" }}>revisión</strong>. Nuestro equipo verificará tu información y te avisaremos por email cuando esté activo.
          </p>
          <div style={{ background: "#f7fafc", borderRadius: 12, padding: "16px 20px", marginBottom: 28, textAlign: "left" }}>
            <div style={{ fontSize: 12, color: "#8fa3b1", fontWeight: 600, marginBottom: 8 }}>PRÓXIMOS PASOS</div>
            {["Cargá la documentación requerida por servicio", "Nuestro equipo revisará tus docs", "¡Empezá a recibir solicitudes!"].map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 10, marginBottom: 8, fontSize: 13, color: "#163244" }}>
                <span style={{ width: 20, height: 20, borderRadius: "50%", background: "#E7A432", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 11, flexShrink: 0 }}>{i + 1}</span>
                {t}
              </div>
            ))}
          </div>
          <button onClick={() => window.location.href = "/prestador/perfil"} style={{ background: "#163244", color: "#fff", border: "none", borderRadius: 10, padding: "14px 32px", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: 15, cursor: "pointer", width: "100%" }}>
            Ir a mi perfil →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f0f5f8", fontFamily: "Montserrat, sans-serif" }}>
      <div>
        <div style={{ background: "#163244", height: 56, display: "flex", alignItems: "center", padding: "0 32px" }}>
          <span style={{ color: "#E7A432", fontWeight: 800, fontSize: 22, letterSpacing: "-0.02em" }}>fixo</span>
        </div>
        <div style={{ background: "#E7A432", height: 5 }} />
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: "40px 20px 80px" }}>
        <div style={{ marginBottom: 32, textAlign: "center" }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "#163244", margin: "0 0 8px", letterSpacing: "-0.02em" }}>
            Registrate como prestador
          </h1>
          <p style={{ color: "#5c7a8a", fontSize: 15, margin: 0 }}>
            Completá tu perfil para empezar a recibir solicitudes de clientes.
          </p>
        </div>

        <StepIndicator current={step} />

        <div style={{ background: "#fff", borderRadius: 16, padding: "36px 40px", boxShadow: "0 4px 24px rgba(22,50,68,0.08)" }}>
          {step === 0 && <StepPerfil    data={form} onChange={handleChange} errors={errors} />}
          {step === 1 && <StepServicios data={form} onChange={handleChange} errors={errors} catalogo={catalogo} loadingCatalog={loadingCatalog} />}
          {step === 2 && <StepCobertura data={form} onChange={handleChange} errors={errors} />}
          {step === 3 && <StepDisponibilidad data={form} onChange={handleChange} errors={errors} />}

          {/* API error */}
          {apiError && (
            <div style={{ background: "#fdecea", border: "1px solid #e57373", borderRadius: 8, padding: "10px 14px", marginTop: 16, fontSize: 13, color: "#c0392b", fontWeight: 600 }}>
              ⚠ {apiError}
            </div>
          )}

          {/* Navigation */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 36, paddingTop: 24, borderTop: "1px solid #e0e9ef" }}>
            {step > 0 ? (
              <button onClick={() => setStep(s => s - 1)} style={{ padding: "12px 24px", borderRadius: 8, border: "2px solid #163244", background: "transparent", color: "#163244", fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                ← Anterior
              </button>
            ) : <div />}

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 12, color: "#8fa3b1" }}>Paso {step + 1} de {STEPS.length}</span>
              <button
                onClick={next}
                disabled={loading}
                style={{ padding: "12px 32px", borderRadius: 8, background: loading ? "#8fa3b1" : "#163244", color: "#fff", border: "none", fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: 15, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8 }}
              >
                {loading ? (
                  <><span style={{ width: 16, height: 16, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> Enviando...</>
                ) : step === STEPS.length - 1 ? "Enviar registro ✓" : "Continuar →"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        input:focus, select:focus, textarea:focus { border-color: #163244 !important; box-shadow: 0 0 0 3px rgba(22,50,68,0.08); }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
